// Turnos de trabajo: catálogo (horario, receso, tolerancia, días laborables)
// y asignación por empleado con vigencia. Nómina → Personal → Turnos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  Campo, DIAS_SEMANA, Modal, Switch, fecha, fieldCls, nom, nomRpc, useDialogoEnLinea,
  type AsignacionTurno, type Turno, hoyISO } from './shared';

const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : '—');
const diasTxt = (dias: number[] | null | undefined) =>
  (dias ?? []).map((d) => DIAS_SEMANA[d - 1]).join(' ');

function Catalogo() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Turno[]>({
    queryKey: ['nom-turnos'],
    queryFn: () => nom.list<Turno>('turnos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const vacio = { codigo: '', nombre: '', hora_entrada: '08:00', hora_salida: '17:00', receso_min: '60', tolerancia_min: '10', dias: [1, 2, 3, 4, 5] as number[], es_activo: true };
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [editId, setEditId] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);

  const abrir = (t?: Turno) => {
    if (t) setForm({ codigo: t.codigo ?? '', nombre: t.nombre, hora_entrada: hhmm(t.hora_entrada), hora_salida: hhmm(t.hora_salida), receso_min: String(t.receso_min), tolerancia_min: String(t.tolerancia_min), dias: t.dias, es_activo: t.es_activo });
    else setForm(vacio);
    setEditId(t?.id ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, hora_entrada: form.hora_entrada, hora_salida: form.hora_salida, receso_min: Number(form.receso_min) || 0, tolerancia_min: Number(form.tolerancia_min) || 0, dias: form.dias, es_activo: form.es_activo };
      return editId ? nom.update('turnos', editId, body) : nom.create('turnos', body);
    },
    onSuccess: () => { toast.success('Turno guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-turnos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nom.update('turnos', id, { deleted_at: new Date().toISOString(), es_activo: false }),
    onSuccess: () => { toast.success('Turno eliminado'); void qc.invalidateQueries({ queryKey: ['nom-turnos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersCat = ['N°', 'Código', 'Nombre', 'Horario', 'Días', 'Receso', 'Tolerancia', 'Estado'];
  const filasCat: Celda[][] = rows.map((t, i): Celda[] => [
    i + 1, t.codigo, t.nombre, `${hhmm(t.hora_entrada)}–${hhmm(t.hora_salida)}`,
    diasTxt(t.dias), Number(t.receso_min), Number(t.tolerancia_min), t.es_activo ? 'Activo' : 'Inactivo',
  ]);

  return (
    <div className="space-y-3">
      {dlg.ui}
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <ExportarBotones nombre={`turnos-catalogo-${fechaLocal()}`} titulo="Turnos de trabajo"
            headers={headersCat} filas={filasCat} colsDerecha={[0, 5, 6]} disabled={rows.length === 0} />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo turno</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Turnos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Horario</th>
              <th className="py-2 pr-3">Días</th><th className="py-2 pr-3 text-right">Receso</th><th className="py-2 pr-3 text-right">Tolerancia</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{t.codigo}</td>
                  <td className="py-2 pr-3">{t.nombre}</td>
                  <td className="py-2 pr-3 font-mono">{hhmm(t.hora_entrada)}–{hhmm(t.hora_salida)}{t.hora_salida <= t.hora_entrada && <span className="text-xs text-muted-foreground ml-1">(+1 día)</span>}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{diasTxt(t.dias)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{t.receso_min} min</td>
                  <td className="py-2 pr-3 text-right font-mono">{t.tolerancia_min} min</td>
                  <td className="py-2 pr-3">{t.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(t)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${t.nombre}"?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(t.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar turno' : 'Nuevo turno'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="TD" /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Hora de entrada *"><input type="time" className={fieldCls} value={form.hora_entrada} onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })} /></Campo>
              <Campo label="Hora de salida *"><input type="time" className={fieldCls} value={form.hora_salida} onChange={(e) => setForm({ ...form, hora_salida: e.target.value })} /></Campo>
              <Campo label="Receso (min)"><input type="number" min="0" className={fieldCls} value={form.receso_min} onChange={(e) => setForm({ ...form, receso_min: e.target.value })} /></Campo>
              <Campo label="Tolerancia de entrada (min)"><input type="number" min="0" className={fieldCls} value={form.tolerancia_min} onChange={(e) => setForm({ ...form, tolerancia_min: e.target.value })} /></Campo>
            </div>
            {form.hora_salida <= form.hora_entrada && <div className="text-xs text-muted-foreground">Turno nocturno: la salida es al día siguiente.</div>}
            <Campo label="Días laborables">
              <div className="flex gap-2">
                {DIAS_SEMANA.map((d, i) => (
                  <button key={d} type="button" aria-pressed={form.dias.includes(i + 1)}
                    className={`w-9 h-9 rounded-md border text-sm font-medium ${form.dias.includes(i + 1) ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:bg-muted'}`}
                    onClick={() => setForm({ ...form, dias: form.dias.includes(i + 1) ? form.dias.filter((x) => x !== i + 1) : [...form.dias, i + 1].sort() })}>
                    {d}
                  </button>
                ))}
              </div>
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || form.dias.length === 0} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Asignaciones() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<AsignacionTurno[]>({
    queryKey: ['nom-turnos-asig'],
    queryFn: () => nomRpc<AsignacionTurno[]>('nom_turnos_asignaciones', {}),
  });
  const { data: turnos = [] } = useQuery<Turno[]>({
    queryKey: ['nom-turnos'],
    queryFn: () => nom.list<Turno>('turnos', '?select=*&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t)) : rows;
  }, [rows, q]);

  const [asignando, setAsignando] = useState<AsignacionTurno | null>(null);
  const [turnoId, setTurnoId] = useState('');
  const [desde, setDesde] = useState(hoyISO());

  const asignar = useMutation({
    mutationFn: () => nomRpc('nom_turno_asignar', { _empleado_id: asignando!.empleado_id, _turno_id: turnoId, _desde: desde }),
    onSuccess: () => { toast.success('Turno asignado'); setAsignando(null); void qc.invalidateQueries({ queryKey: ['nom-turnos-asig'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const sinTurno = rows.filter((r) => !r.turno_id).length;

  const headersAsig = ['N°', 'Código', 'Empleado', 'Turno', 'Horario', 'Días', 'Desde'];
  const filasAsig: Celda[][] = filas.map((r, i): Celda[] => [
    i + 1, r.empleado_codigo, r.razon_social, r.turno ?? 'Sin turno',
    r.turno ? `${hhmm(r.hora_entrada)}–${hhmm(r.hora_salida)}` : '—', diasTxt(r.dias), fecha(r.desde),
  ]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
        {sinTurno > 0 && <span className="px-2 py-1 rounded-full text-xs bg-pendiente-soft text-pendiente">{sinTurno} sin turno</span>}
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        <div className="ml-auto">
          <ExportarBotones nombre={`turnos-asignaciones-${fechaLocal()}`} titulo="Turnos de trabajo"
            headers={headersAsig} filas={filasAsig} colsDerecha={[0]} disabled={filas.length === 0} />
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Asignaciones ({filas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Turno</th>
              <th className="py-2 pr-3">Horario</th><th className="py-2 pr-3">Días</th><th className="py-2 pr-3">Desde</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.empleado_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.empleado_codigo}</td>
                  <td className="py-2 pr-3">{r.razon_social}<div className="text-xs text-muted-foreground font-mono">{r.identificacion}</div></td>
                  <td className="py-2 pr-3">{r.turno ?? <span className="text-pendiente">Sin turno</span>}</td>
                  <td className="py-2 pr-3 font-mono">{r.turno ? `${hhmm(r.hora_entrada)}–${hhmm(r.hora_salida)}` : '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{diasTxt(r.dias)}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(r.desde)}</td>
                  <td className="py-2 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Asignar turno"
                      onClick={() => { setAsignando(r); setTurnoId(r.turno_id ?? ''); setDesde(hoyISO()); }}>
                      <CalendarPlus className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {asignando && (
        <Modal title={`Asignar turno — ${asignando.razon_social}`} onClose={() => setAsignando(null)}>
          <div className="space-y-3">
            <Campo label="Turno *">
              <select className={fieldCls} value={turnoId} onChange={(e) => setTurnoId(e.target.value)}>
                <option value="">— Selecciona —</option>
                {turnos.map((t) => <option key={t.id} value={t.id}>{t.nombre} ({hhmm(t.hora_entrada)}–{hhmm(t.hora_salida)})</option>)}
              </select>
            </Campo>
            <Campo label="Vigente desde *"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
            <div className="text-xs text-muted-foreground">La asignación anterior se cierra automáticamente el día previo.</div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAsignando(null)}>Cancelar</Button>
              <Button isLoading={asignar.isPending} disabled={!turnoId || !desde} onClick={() => asignar.mutate()}>Asignar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Turnos() {
  const [tab, setTab] = useState<'catalogo' | 'asignaciones'>('catalogo');
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Turnos de trabajo</h1>
      <div className="flex gap-1 border-b">
        {([['catalogo', 'Catálogo'], ['asignaciones', 'Asignaciones']] as const).map(([k, v]) => (
          <button key={k} aria-pressed={tab === k}
            className={`px-4 py-2 text-sm rounded-t-md ${tab === k ? 'bg-card border border-b-0 font-medium' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setTab(k)}>{v}</button>
        ))}
      </div>
      {tab === 'catalogo' ? <Catalogo /> : <Asignaciones />}
    </div>
  );
}
